import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, Clock, CalendarDays, HeartPulse, User, LogOut, BookOpen, ClipboardList, Briefcase, Layers } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import api from "@/lib/apiClient";
import { BrandLogo, themeBg } from "@/components/BrandLogo";
import { ImpersonationBanner } from "@/layouts/AdminLayout";

const TAB_CLS = "relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-semibold uppercase tracking-wide transition-colors";

const GROUPS = {
  leave: { label: "Leave", icon: CalendarDays, items: [
    { to: "/app/leave", label: "Leave", icon: CalendarDays, key: null },
    { to: "/app/sickness", label: "Sick leave", icon: HeartPulse, key: "sick" },
  ] },
  resources: { label: "Resources", icon: Layers, items: [
    { to: "/app/resources", label: "Library", icon: BookOpen, key: "unread" },
    { to: "/app/forms", label: "Forms", icon: ClipboardList, key: "forms", module: "/admin/forms" },
  ] },
};

const Dot = ({ n, testid }) => (n > 0 ? <span data-testid={testid} className="min-w-[16px] h-4 px-1 rounded-full bg-rose-600 text-white text-[9px] leading-4 text-center">{n > 9 ? "9+" : n}</span> : null);

function useBadges() {
  const [b, setB] = useState({ sick: 0, unread: 0, forms: 0 });
  useEffect(() => {
    const refresh = () => api.get("/resources/feed").then((r) => setB((p) => ({ ...p, unread: (r.data.resources || []).filter((x) => !x.read).length }))).catch(() => {});
    window.addEventListener("library-read", refresh);
    return () => { window.removeEventListener("library-read", refresh); };
  }, []);
  useEffect(() => {
    api.get("/sick-leave").then((r) => setB((p) => ({ ...p, sick: (r.data || []).filter((s) => !s.actual_return).length }))).catch(() => {});
    api.get("/resources/feed").then((r) => setB((p) => ({ ...p, unread: (r.data.resources || []).filter((x) => !x.read).length }))).catch(() => {});
    api.get("/forms/my").then((r) => setB((p) => ({ ...p, forms: (r.data.forms || []).filter((f) => !f.done).length }))).catch(() => {});
  }, []);
  return b;
}

function GroupTab({ id, company, badges }) {
  const g = GROUPS[id];
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const items = g.items.filter((i) => !i.module || company?.enabled_modules?.[i.module] !== false);
  const active = items.some((i) => pathname.startsWith(i.to));
  const total = items.reduce((s, i) => s + (i.key ? badges[i.key] : 0), 0);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`tab-${id}`} className={cn(TAB_CLS, active || open ? "text-amber-600" : "text-zinc-400")}>
          <g.icon className="h-5 w-5" />{g.label}
          {total > 0 && <span data-testid={`tab-${id}-badge`} className="absolute top-1.5 right-[calc(50%-18px)] h-2.5 w-2.5 rounded-full bg-rose-600 ring-2 ring-white" />}
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" sideOffset={8} className="w-48 p-1.5" data-testid={`tab-${id}-menu`}>
        {items.map((i) => (
          <NavLink key={i.to} to={i.to} onClick={() => setOpen(false)} data-testid={`menu-${i.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) => cn("flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors", isActive ? "bg-amber-50 text-amber-700" : "text-zinc-700 hover:bg-zinc-100")}>
            <i.icon className="h-4 w-4" /><span className="flex-1">{i.label}</span>
            {i.key && <Dot n={badges[i.key]} testid={`badge-${i.key}`} />}
          </NavLink>
        ))}
      </PopoverContent>
    </Popover>
  );
}

const Tab = ({ to, end, label, icon: Icon }) => (
  <NavLink to={to} end={end} data-testid={`tab-${label.toLowerCase()}`} className={({ isActive }) => cn(TAB_CLS, isActive ? "text-amber-600" : "text-zinc-400")}>
    <Icon className="h-5 w-5" />{label}
  </NavLink>
);

export default function EmployeeLayout({ children, title }) {
  const { user, employee, company, logout, switchMode } = useAuth();
  const nav = useNavigate();
  const badges = useBadges();
  const doLogout = async () => { await logout(); nav("/login"); };
  const isAdminWork = user?.real_role === "kqm_admin";
  const toManage = async () => { await switchMode("manager"); nav(isAdminWork ? "/admin" : "/manager"); };
  return (
    <div className={cn("min-h-screen bg-[#FAFAFA] pb-20", user?._impersonation?.active && "pt-10")}>
      <ImpersonationBanner />
      <header className={cn("sticky z-30 text-white h-14 flex items-center justify-between px-4", user?._impersonation?.active ? "top-10" : "top-0")} style={{ backgroundColor: themeBg(company?.primary_color) }}>
        <div className="flex items-center gap-2">
          <BrandLogo company={company} dark className="h-6" />
        </div>
        <div className="flex items-center gap-3">
          {user?.manager_mode === "employee" && <button data-testid="switch-to-manage-btn" onClick={toManage} className="flex items-center gap-1 text-xs font-semibold bg-amber-500 text-black px-2.5 py-1 rounded-full hover:bg-amber-400 transition-colors"><Briefcase className="h-3.5 w-3.5" />{isAdminWork ? "Admin" : "Manage"}</button>}
          <span className="text-xs text-zinc-300">{employee?.preferred_name || user?.name?.split(" ")[0]}</span>
          <button data-testid="logout-btn" onClick={doLogout}><LogOut className="h-5 w-5 text-zinc-300" /></button>
        </div>
      </header>
      <main className="px-4 py-5 max-w-xl mx-auto">{children}</main>
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-zinc-200 flex">
        <Tab to="/app" end label="Home" icon={Home} />
        <Tab to="/app/timesheets" label="Timesheets" icon={Clock} />
        <GroupTab id="leave" company={company} badges={badges} />
        <GroupTab id="resources" company={company} badges={badges} />
        <Tab to="/app/profile" label="Profile" icon={User} />
      </nav>
    </div>
  );
}
